import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "../components/Admin.css";
import "../components/UserInfo.css";
import "../generics/PageCharts.css";
import { FormattedMessage } from "react-intl";
import messages from "../lang/preferences";
import Axios from 'axios';
import url from '../constants/urlsPaths';
import ppeFullLogo from '../ressources/logo-with-text-green.svg';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { computeAttitudeDetails } from '../utils/attitudes';
import allNorms from '../norms.json';

// Calculate percentile for an attitude score based on normative data
const calculateAttitudePercentile = (attitudeName, score, attitudeDetails, traits) => {
  if (!attitudeDetails || !attitudeDetails[attitudeName] || !traits) return null;
  
  const attDetail = attitudeDetails[attitudeName];
  if (!attDetail.rows || attDetail.rows.length === 0) return null;
  
  // Use maleMiddleManagement as reference population (most common)
  const norms = allNorms.maleMiddleManagement;
  
  // Calculate weighted average percentile across all indicators in this attitude
  let totalWeight = 0;
  let weightedPercentileSum = 0;
  
  attDetail.rows.forEach(row => {
    const traitKey = row.backendKey;
    const traitValue = traits[traitKey];
    
    if (traitValue != null && norms[traitKey]) {
      const [lower, mid, upper] = norms[traitKey];
      let percentile;
      
      // Calculate percentile based on position in norm distribution
      if (traitValue <= lower) {
        percentile = 16; // Below 1st standard deviation
      } else if (traitValue <= mid) {
        // Linear interpolation between lower (16th) and mid (50th)
        percentile = 16 + ((traitValue - lower) / (mid - lower)) * 34;
      } else if (traitValue <= upper) {
        // Linear interpolation between mid (50th) and upper (84th)
        percentile = 50 + ((traitValue - mid) / (upper - mid)) * 34;
      } else {
        // Above 84th percentile
        percentile = 84 + Math.min(((traitValue - upper) / upper) * 16, 16);
      }
      
      weightedPercentileSum += percentile * row.normalizedWeight;
      totalWeight += row.normalizedWeight;
    }
  });
  
  return totalWeight > 0 ? Math.round(weightedPercentileSum / totalWeight) : null;
};

// Helper to safely render FormattedMessage
function SafeFormattedMessage({ msgKey }) {
  const msg = messages[msgKey];
  const valid = msg && typeof msg.id === "string" && msg.id.length > 0;
  if (valid) {
    return <FormattedMessage {...msg} />;
  }
  return msgKey || "";
}

const attitudeNameMap = {
  'Qualite': 'ATTITUDES_QUALITY_NAME',
  'Respect': 'ATTITUDES_RESPECT_NAME',
  'Cooperation': 'ATTITUDES_COOPERATION_NAME',
  'Engagement': 'ATTITUDES_ENGAGEMENT_NAME',
  'Ouverture': 'ATTITUDES_OPENNESS_NAME'
};

// Calculate statistics for all attitudes (for reference group analysis)
const calculateAttitudeStats = (scores) => {
  if (!scores || scores.length === 0) return null;
  
  const stats = {};
  const attitudeNames = ['Qualite', 'Respect', 'Cooperation', 'Engagement', 'Ouverture'];
  
  // Reference data calculated from high-performer candidates grouped by attitudes
  // Source: backend/scripts/analyze_high_performers.js
  // Generated from Liste_par_attitude_fondamentale.xlsx candidate lists
  // Each attitude's reference group consists of candidates specifically linked to that attitude
  const referenceData = {
    'Qualite': { mean: 3.08, median: 3.04, stdDev: 0.36 },
    'Respect': { mean: 3.09, median: 3.21, stdDev: 0.29 },
    'Cooperation': { mean: 3.45, median: 3.59, stdDev: 0.33 },
    'Engagement': { mean: 3.52, median: 3.65, stdDev: 0.33 },
    'Ouverture': { mean: 1.52, median: 1.54, stdDev: 0.23 }
  };
  
  attitudeNames.forEach(name => {
    const att = scores.find(s => s.name === name);
    if (att && referenceData[name]) {
      const score = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
      const ref = referenceData[name];
      
      // Calculate Z-score: (score - mean) / stdDev
      const zScore = (score - ref.mean) / ref.stdDev;
      
      // Calculate difference from reference mean and median
      const diffFromMean = score - ref.mean;
      const diffFromMedian = score - ref.median;
      
      stats[name] = {
        score: score,
        mean: ref.mean,
        median: ref.median,
        stdDev: ref.stdDev,
        zScore: zScore,
        diffFromMean: diffFromMean,
        diffFromMedian: diffFromMedian
      };
    }
  });
  
  return stats;
};

// Fetch attitude scores for a user from backend results
const fetchAttitudeScores = async (userId) => {
  if (!userId) return [];
  try {
    const response = await Axios.post(url.getResults, { code: userId }, {
      headers: {
        "x-access-token": (() => {
          const token = sessionStorage.getItem("token");
          if (!token || token === "null" || token === "undefined") {
            return null;
          }
          try {
            return JSON.parse(token);
          } catch (e) {
            return null;
          }
        })()
      }
    });

    // eslint-disable-next-line no-console
    console.log('DEBUG: Full response.data =', response.data);

    let attitudes = response.data?.attitudes || response.data?.results?.attitudes;
    if (!attitudes && Array.isArray(response.data?.results)) {
      for (const r of response.data.results) {
        if (r.attitudes) { attitudes = r.attitudes; break; }
      }
    }

    let attitudeDetails = response.data?.attitudeDetails || response.data?.results?.attitudeDetails || null;
    let rawTraits = null; // Store raw trait values for percentile calculation
    
    // Extract trait values from results array
    if (Array.isArray(response.data?.results)) {
      const personality = response.data.results.find(r => r.title === 'SCOPEREPORT_PERSONALITY');
      const coping = response.data.results.find(r => r.title === 'SCOPEREPORT_COPING');
      const action = response.data.results.find(r => r.title === 'SCOPEREPORT_ACTIONORIENTATION');
      const valuesWork = response.data.results.find(r => r.title === 'SCOPEREPORT_VALUEPREFERENCES');
      const valuesPersonal = response.data.results.find(r => r.title === 'SCOPEREPORT_VALUEPREFERENCES2');

      if (personality && coping && action && valuesWork && valuesPersonal) {
        const p = personality.data || [];
        const c = coping.data || [];
        const a = action.data || [];
        const vw = valuesWork.data || [];
        const vp = valuesPersonal.data || [];

        rawTraits = {
          extraversion: parseFloat(p[0]?.user) || 0,
          excitementSeeking: parseFloat(p[1]?.user) || 0,
          impulsivity: parseFloat(p[2]?.user) || 0,
          pessimism: parseFloat(p[3]?.user) || 0,
          selfAssurance: parseFloat(p[4]?.user) || 0,
          dominance: parseFloat(p[5]?.user) || 0,
          aggressiveness: parseFloat(p[6]?.user) || 0,
          conscientiousness: parseFloat(p[7]?.user) || 0,
          capacityForReasoning: parseFloat(p[8]?.user) || 0,
          affectRegulation: parseFloat(a[1]?.user) || 0,
          expressiveness: parseFloat(a[0]?.user) || 0,
          stressResilience: parseFloat(a[2]?.user) || 0,
          wishfulThinking: parseFloat(c[7]?.user) || 0,
          seekingSocialSupport: parseFloat(c[6]?.user) || 0,
          affection: parseFloat(vp[4]?.user) || 0,
          harmony: parseFloat(vp[5]?.user) || 0,
          morality: parseFloat(vp[2]?.user) || 0,
          integrity: parseFloat(vp[3]?.user) || 0,
          enjoyWork: parseFloat(vw[0]?.user) || 0,
          intellectualStimulation: parseFloat(vw[3]?.user) || 0,
          taskVariability: parseFloat(vw[5]?.user) || 0,
          security: parseFloat(vw[2]?.user) || 0,
        };
        
        // eslint-disable-next-line no-console
        console.log('DEBUG: Extracted rawTraits from results:', rawTraits);
      }
    }
    
    if (!attitudeDetails && rawTraits) {
      // If backend didn't provide attitudeDetails, compute them client-side
      try {
        attitudeDetails = computeAttitudeDetails(rawTraits);
        // eslint-disable-next-line no-console
        console.groupCollapsed && console.groupCollapsed('Attitude calculation details (computed client-side)');
        // eslint-disable-next-line no-console
        Object.keys(attitudeDetails).forEach(att => {
          // eslint-disable-next-line no-console
          console.group && console.group(att + ' — score: ' + (attitudeDetails[att].score != null ? attitudeDetails[att].score.toFixed(3) : 'n/a'));
          // eslint-disable-next-line no-console
          console.table && console.table(attitudeDetails[att].rows.map(r => ({ indicator: r.indicator, backendKey: r.backendKey, value: r.value, weight: r.weightPercent, normalizedWeight: Number(r.normalizedWeight.toFixed(2)), polarity: r.polarity, contrib: Number(r.contrib.toFixed(3)) })));
          // eslint-disable-next-line no-console
          console.groupEnd && console.groupEnd();
        });
        // eslint-disable-next-line no-console
        console.groupEnd && console.groupEnd();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to compute attitude details client-side:', e);
      }
    } else if (attitudeDetails) {
      // Pretty-print server-provided details for debugging (grouped + table)
      try {
        // eslint-disable-next-line no-console
        console.groupCollapsed && console.groupCollapsed('Attitude calculation details (server)');
        // eslint-disable-next-line no-console
        Object.keys(attitudeDetails).forEach(att => {
          const entry = attitudeDetails[att] || {};
          const rows = Array.isArray(entry.rows) ? entry.rows : [];
          // eslint-disable-next-line no-console
          console.group && console.group(att + ' — score: ' + (entry.score != null ? Number(entry.score).toFixed(3) : 'n/a'));
          // eslint-disable-next-line no-console
          console.table && console.table(rows.map(r => ({ indicator: r.indicator, backendKey: r.backendKey, value: r.value, weight: r.weightPercent, normalizedWeight: r.normalizedWeight != null ? Number(r.normalizedWeight.toFixed(2)) : r.normalizedWeight, polarity: r.polarity, contrib: r.contrib != null ? Number(r.contrib.toFixed(3)) : r.contrib })));
          // eslint-disable-next-line no-console
          console.groupEnd && console.groupEnd();
        });
        // eslint-disable-next-line no-console
        console.groupEnd && console.groupEnd();
      } catch (e) {
        // Fallback to simple log if something unexpected is present
        // eslint-disable-next-line no-console
        console.log('Attitude details (per-attitude):', attitudeDetails);
      }
    }

    if (!attitudes) return { attitudes: [], rawTraits };
    if (!Array.isArray(attitudes)) {
      attitudes = Object.entries(attitudes).filter(([name, value]) => value !== null).map(([name, value]) => {
        const score = value && typeof value === 'object' ? value.score : (typeof value === 'number' ? value : 0);
        const description = value && typeof value === 'object' ? value.description : '';
        return { name, score, description };
      });
    }
    
    // Add percentile information to each attitude
    // eslint-disable-next-line no-console
    console.log('DEBUG: rawTraits =', rawTraits);
    // eslint-disable-next-line no-console
    console.log('DEBUG: attitudes =', attitudes);
    // eslint-disable-next-line no-console
    console.log('DEBUG: attitudeDetails =', attitudeDetails);
    
    if (rawTraits && attitudes.length > 0) {
      attitudes = attitudes.map(att => {
        const percentile = calculateAttitudePercentile(att.name, att.score, attitudeDetails, rawTraits);
        // eslint-disable-next-line no-console
        console.log(`DEBUG: Percentile for ${att.name} = ${percentile}`);
        return {
          ...att,
          percentile
        };
      });
    }
    
    return { attitudes, rawTraits };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch attitude scores:', e);
    return [];
  }
};

export default function AttitudesPage(props) {
  const location = useLocation();
  const user = props.user || location.state?.user || {};
  const userId = user.code;
  const [, setTraitsState] = useState(location.state?.traits || {});
  const onBack = props.onBack || (() => {});
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const embedded = props.embedded === true;
  // UI mode: when true show only the final interpretation table (hide other tables and labels)
  const showOnlyInterpretation = true;

  useEffect(() => {
    let mounted = true;
    fetchAttitudeScores(userId).then((data) => {
      if (!mounted) return;
      // fetchAttitudeScores now returns { attitudes, rawTraits }
      setScores(data.attitudes || []);
      setTraitsState(data.rawTraits || {});
      setLoading(false);
    }).catch(() => {
      if (!mounted) return;
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [userId]);

  // Page 1: Introduction and Raw Scores
  const page1Jsx = (
    <div className="PageCharts">
      <div className="header-logo-box">
          <img
            alt="ppe-logo"
            className="header-logo-img"
            src={ppeFullLogo}
          />
      </div>
      <div>
        <div className="PageCharts-chart">
          <div style={{
            height: '845pt',
            margin: '0 auto',
            paddingTop: '80px',
            paddingRight: '20px',
            paddingLeft: '20px',
            width:'80%'
          }}>
           { !embedded && <h1 style={{
              color: 'var(--ppe-green)',
              fontSize: '28px',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '10px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              <SafeFormattedMessage msgKey="ATTITUDES_REPORT_TITLE" />
            </h1>
          }
            {user && !embedded && (
              <div style={{
                textAlign: 'center',
                marginBottom: '30px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h3 style={{ color: '#495057', margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_ASSESSMENT_FOR_CANDIDATE" /> {user.code}
                </h3>
                <span style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: '14px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_REPORT_GENERATED_ON" /> {new Date().toLocaleDateString()}TREE
                </span>
              </div>
            )}

            <div style={{ marginBottom: '40px', paddingRight: '10px', lineHeight: '1.3', fontSize: '16px', color: '#495057' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '22px', fontWeight: 'bold', marginBottom: '16px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_FUNDAMENTAL_ATTITUDES" />
                <span className="inline font-16"> - {((props.localeDisplay || (user && user.language)) === 'fr') ? 'Définitions' : 'Definitions'}</span>
              </h2>
              <div style={{ marginBottom: '-15px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_FUNDAMENTAL_ATTITUDES_DESC" />
              </div>
              <ul style={{ marginLeft: '20px', marginBottom: '20px' }}>
                <li><SafeFormattedMessage msgKey="ATTITUDES_CLARIFY_EXPECTED_BEHAVIORS" /></li>
                <li><SafeFormattedMessage msgKey="ATTITUDES_FACILITATE_COMMON_LIFE" /></li>
                <li><SafeFormattedMessage msgKey="ATTITUDES_HARMONIZE_ACTIONS" /></li>
                <li><SafeFormattedMessage msgKey="ATTITUDES_KEY_STAGE_EMPLOYEE_LIFECYCLE" /></li>
              </ul>
              <div style={{ marginBottom: '-15px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_FIVE_ATTITUDES" />
              </div>
              <ul style={{ marginLeft: '20px', marginBottom: '20px' }}>
                <li><b><SafeFormattedMessage msgKey="ATTITUDES_QUALITY_NAME" /></b> <SafeFormattedMessage msgKey="ATTITUDES_QUALITY_DESC" /></li>
                <li><b><SafeFormattedMessage msgKey="ATTITUDES_RESPECT_NAME" /></b> <SafeFormattedMessage msgKey="ATTITUDES_RESPECT_DESC" /></li>
                <li><b><SafeFormattedMessage msgKey="ATTITUDES_COOPERATION_NAME" /></b> <SafeFormattedMessage msgKey="ATTITUDES_COOPERATION_DESC" /></li>
                <li><b><SafeFormattedMessage msgKey="ATTITUDES_ENGAGEMENT_NAME" /></b> <SafeFormattedMessage msgKey="ATTITUDES_ENGAGEMENT_DESC" /></li>
                <li><b><SafeFormattedMessage msgKey="ATTITUDES_OPENNESS_NAME" /></b> <SafeFormattedMessage msgKey="ATTITUDES_OPENNESS_DESC" /></li>
              </ul>
              <div style={{ marginBottom: '12px' }}>
                  {((props.localeDisplay || (user && user.language)) === 'fr') ? (
                    <>Il n'y a pas de "bon" ou de "mauvais" score : chaque résultat, à la page suivante, est une photographie des attitudes à un moment donné, relative au groupe de comparaison. Il invite à la réflexion plutôt qu'au verdict — ces attitudes peuvent évoluer avec le temps, le contexte et l'intention.</>
                  ) : (
                    <>There is no "good" or "bad" score: each result, on the next page, is a snapshot of attitudes at a given moment, relative to the comparison group. It invites reflection rather than judgment — these attitudes can evolve over time, with context and intention.</>
                  )}
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px', fontSize: '18px', color: '#6c757d' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_LOADING_DATA" />
              </div>
            ) : !userId ? (
              <div style={{ color: 'red', textAlign: 'center', padding: '40px', fontSize: '16px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_NO_USER_SELECTED" />
              </div>
            ) : (
              <div>
                  <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: 'var(--ppe-green-10)', borderRadius: '8px', border: '1px solid #e1bee7', display: showOnlyInterpretation ? 'none' : undefined }}>
                  {/* <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                      <SafeFormattedMessage msgKey="ATTITUDES_GAP_REFERENCE_GROUP" />
                    </h3>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                      <SafeFormattedMessage msgKey="ATTITUDES_ABSOLUTE_DIFFERENCE_HIGH_PERFORMERS" />
                    </p>*/}
                    { !showOnlyInterpretation && (() => {
                      const stats = calculateAttitudeStats(scores);
                      if (!stats) return null;
                      
                      return (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'var(--ppe-green)', color: 'white' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_ATTITUDE" /></th>

                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_INTERPRETATION" /></th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(stats).map(([name, data], idx) => {
                                const score = data.score;
                                let interpretation = '';
                                let bgColor = '#fff';

                                // If P25/P75 are available use percentile-based coloring:
                                // > P75 => green (Above), P25..P75 => orange (Comparable), < P25 => red (Below)
                                // equality is treated as less restrictive (falls into middle band)
                                if (data.p25 != null && data.p75 != null) {
                                  if (score > data.p75) {
                                    interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_ABOVE" />;
                                    bgColor = '#c8e6c9';
                                  } else if (score >= data.p25 && score <= data.p75) {
                                    interpretation = <SafeFormattedMessage msgKey="ATTITUDES_COMPARABLE" />;
                                    bgColor = '#fff9c4';
                                  } else {
                                    interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_BELOW" />;
                                    bgColor = '#ffccbc';
                                  }
                                } else {
                                  // Simplified fallback to three levels: clearly above, comparable, clearly below
                                  const diff = data.diffFromMean;
                                  if (diff >= 0.5) {
                                    interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_ABOVE" />;
                                    bgColor = '#c8e6c9';
                                  } else if (diff <= -0.5) {
                                    interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_BELOW" />;
                                    bgColor = '#ffccbc';
                                  } else {
                                    interpretation = <SafeFormattedMessage msgKey="ATTITUDES_COMPARABLE" />;
                                    bgColor = '#fff9c4';
                                  }
                                }
                                
                                return (
                                  <tr key={name} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                                    <td style={{ padding: '12px', fontWeight: '600', borderBottom: '1px solid #e0e0e0' }}><SafeFormattedMessage msgKey={attitudeNameMap[name]} /></td>

                                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', color: '#6f6f6fff', backgroundColor: bgColor, fontWeight: '500' }}>
                                      {interpretation}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })() }
                  </div>

              </div>
              
            )}
          </div>
                   <div className="pdf-footer">
            <SafeFormattedMessage msgKey="PAGE" /> {typeof props.pageNumber === 'number' ? props.pageNumber : 14}
            <div className="pdf-footer-separator">
              <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
            </div>{" "}
            {user.code}
            <div className="pdf-footer-separator">
              <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {new Date(user.date).toLocaleDateString(
                  props.localeDisplay || undefined,
                  { day: "2-digit", month: "long", year: "numeric" }
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Page 2: Percentile Ranking
  const page2Jsx = scores.length > 0 ? (
    <div className="PageCharts">
      <div>
        <div className="PageCharts-chart">
          <div style={{
            height: '845pt',
            margin: '0 auto',
            padding: '40px 20px',
            width:'80%'
          }}>
            {!loading && userId && (
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '20px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>
                  { !embedded  && <span><SafeFormattedMessage msgKey="ATTITUDES_PERCENTILE_RANKING" /></span> }
                </h3>
                <p style={{ textAlign: 'center', fontSize: '14px', color: '#666', marginBottom: '20px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_RELATIVE_POSITION_REFERENCE_POPULATION" />
                </p>
                {scores.some(s => s.percentile != null) ? (
                  <div style={{ height: '280px', width: '100%', marginLeft: '-100px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {(() => {
                        const chartData = scores
                          .filter(att => att.percentile != null)
                          .map(att => {
                            const percentile = att.percentile;
                            const rawScore = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
                            let color = '#4caf50'; // Green
                            if (percentile < 30) color = '#ff9800'; // Orange
                            if (percentile < 16) color = '#f44336'; // Red
                            if (percentile >= 84) color = '#2e7d32'; // Dark green
                            
                            return { 
                              name: att.name, 
                              value: percentile, 
                              percentile: percentile,
                              rawScore: rawScore.toFixed(2),
                              color: color
                            };
                          });
                        return (
                          <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis 
                              type="number" 
                              domain={[0, 100]} 
                              ticks={[0, 16, 30, 50, 70, 84, 100]}
                              label={{ value: 'Percentile', position: 'insideBottom', offset: -5 }}
                            />
                            <YAxis type="category" dataKey="name" width={150} />
                            <Tooltip 
                              formatter={(value, name, props) => [
                                `${props.payload.percentile}e percentile (Score: ${props.payload.rawScore})`, 
                                'Position'
                              ]} 
                              labelFormatter={(label) => `Attitude: ${label}`}
                              contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }}
                            />
                            <Bar dataKey="value">
                              {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        );
                      })()}
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p style={{ textAlign: 'center', color: '#6c757d' }}><SafeFormattedMessage msgKey="ATTITUDES_PERCENTILE_DATA_NOT_AVAILABLE" /></p>
                )}
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', fontSize: '13px', color: '#555' }}>
                  <strong><SafeFormattedMessage msgKey="ATTITUDES_LEGEND" /></strong>
                  <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '8px', flexWrap: 'wrap' }}>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#2e7d32', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_TOP_16_EXCELLENT" /></span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4caf50', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_ABOVE_AVERAGE" /></span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ff9800', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SLIGHTLY_BELOW" /></span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f44336', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_DEVELOPMENT_ZONE" /></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;
  const additionalAnalysesJsx = scores.length > 0 ? (
    <>
      {/* Page 3: Expanded X-Axis */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_1" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_RAW_SCORES_EXPANDED_VIEW" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_ZOOM_REAL_RANGE" />
                </p>
                <div style={{ height: '260px', width: '100%', marginLeft: '-100px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {(() => {
                      const chartData = scores.map(att => {
                        const rawScore = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
                        return { name: att.name, value: rawScore, rawScore: rawScore.toFixed(2) };
                      });
                      return (
                        <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                          <XAxis type="number" domain={[1.0, 4.0]} ticks={[1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0]} label={{ value: 'Score', position: 'insideBottom', offset: -5 }} />
                          <YAxis type="category" dataKey="name" width={150} />
                          <Tooltip formatter={(value, name, props) => [props.payload.rawScore, 'Score']} labelFormatter={(label) => `Attitude: ${label}`} />
                          <Bar dataKey="value">
                            {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={'#2196f3'} />)}
                          </Bar>
                        </BarChart>
                      );
                    })()}
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
{ <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>}
      {/* Page 4: Standard Deviation Bands */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_2" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffe0b2' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_POSITION_RELATIVE_HIGH_PERFORMERS_MEAN" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_REFERENCE_HIGH_PERFORMERS_MANAGEMENT" />
                </p>
                {(() => {
                  const stats = calculateAttitudeStats(scores);
                  if (!stats) return null;
                  
                  const chartData = Object.entries(stats).map(([name, data]) => {
                    let category = 'Moyenne';
                    let color = '#ffc107';
                    
                    if (data.zScore >= 1) {
                      category = 'Excellence (≥+1 SD)';
                      color = '#2e7d32';
                    } else if (data.zScore >= 0) {
                      category = 'Au-dessus (+0 à +1 SD)';
                      color = '#4caf50';
                    } else if (data.zScore >= -1) {
                      category = 'En-dessous (0 à -1 SD)';
                      color = '#ff9800';
                    } else {
                      category = 'Développement (<-1 SD)';
                      color = '#f44336';
                    }
                    
                    return { name, value: data.score, mean: data.mean, zScore: data.zScore.toFixed(2), category, color };
                  });
                  
                  return (
                    <>
                      <div style={{ height: '280px', width: '100%', marginLeft: '-100px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis type="number" domain={[1.0, 4.5]} ticks={[1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5]} label={{ value: 'Score', position: 'insideBottom', offset: -5 }} />
                            <YAxis type="category" dataKey="name" width={150} />
                            <Tooltip 
                              formatter={(value, name, props) => [`Score: ${props.payload.value.toFixed(2)} | Z-score: ${props.payload.zScore} | ${props.payload.category}`, 'Analysis']}
                              labelFormatter={(label) => `Attitude: ${label}`}
                            />
                            <Bar dataKey="value">
                              {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', fontSize: '13px', color: '#555' }}>
                        <strong><SafeFormattedMessage msgKey="ATTITUDES_LEGEND_HIGH_PERFORMERS" /></strong>
                        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '8px', flexWrap: 'wrap' }}>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#2e7d32', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_EXCELLENCE" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4caf50', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_ABOVE" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ffc107', marginRight: '15px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_AVERAGE" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ff9800', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_BELOW" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f44336', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_DEVELOPMENT" /></span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
{ <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>}
      {/* Page 5: Z-Score Visualization */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_3" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_Z_SCORES" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_STANDARDIZED_POSITION" />
                </p>
                {(() => {
                  const stats = calculateAttitudeStats(scores);
                  if (!stats) return null;
                  
                  const chartData = Object.entries(stats).map(([name, data]) => ({
                    name,
                    value: data.zScore,
                    zScore: data.zScore.toFixed(2),
                    rawScore: data.score.toFixed(2)
                  }));
                  
                  return (
                    <div style={{ height: '260px', width: '100%', marginLeft: '-100px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                          <XAxis type="number" domain={[-2, 2]} ticks={[-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2]} label={{ value: 'Z-Score (Écarts-types)', position: 'insideBottom', offset: -5 }} />
                          <YAxis type="category" dataKey="name" width={150} />
                          <Tooltip 
                            formatter={(value, name, props) => [`Z-score: ${props.payload.zScore} (Raw score: ${props.payload.rawScore})`, 'Normalized Position']}
                            labelFormatter={(label) => `Attitude: ${label}`}
                          />
                          <Bar dataKey="value">
                            {chartData.map((entry, index) => {
                              const z = parseFloat(entry.zScore);
                              let color = '#2196f3';
                              if (z >= 1) color = '#4caf50';
                              else if (z >= 0) color = '#8bc34a';
                              else if (z >= -1) color = '#ff9800';
                              else color = '#f44336';
                              return <Cell key={`cell-${index}`} fill={color} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
{ <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>}
      {/* Page 6: Reference Group Comparison Table */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'88%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_4" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#f3e5f5', borderRadius: '8px', border: '1px solid #e1bee7' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_GAP_REFERENCE_GROUP" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_ABSOLUTE_DIFFERENCE_HIGH_PERFORMERS" />
                </p>
                {(() => {
                  const stats = calculateAttitudeStats(scores);
                  if (!stats) return null;
                  
                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#9c27b0', color: 'white' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_ATTITUDE" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_CANDIDATE_SCORE" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_REFERENCE_MEAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_REFERENCE_MEDIAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_GAP_MEAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_GAP_MEDIAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_INTERPRETATION" /></th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(stats).map(([name, data], idx) => {
                            const diff = data.diffFromMean;
                            let interpretation = '';
                            let bgColor = '#fff';
                            
                            if (diff >= 0.5) {
                              interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_ABOVE" />;
                              bgColor = '#c8e6c9';
                            } else if (diff <= -0.5) {
                              interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_BELOW" />;
                              bgColor = '#ffccbc';
                            } else {
                              interpretation = <SafeFormattedMessage msgKey="ATTITUDES_COMPARABLE" />;
                              bgColor = '#fff9c4';
                            }
                            
                            return (
                              <tr key={name} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                                <td style={{ padding: '12px', fontWeight: '600', borderBottom: '1px solid #e0e0e0' }}><SafeFormattedMessage msgKey={attitudeNameMap[name]} /></td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{data.score.toFixed(2)}</td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{data.mean.toFixed(2)}</td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{data.median.toFixed(2)}</td>
                                <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', color: diff >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                  {diff >= 0 ? '+' : ''}{diff.toFixed(2)}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', color: data.diffFromMedian >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                  {data.diffFromMedian >= 0 ? '+' : ''}{data.diffFromMedian.toFixed(2)}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', backgroundColor: bgColor, fontWeight: '500' }}>
                                  {interpretation}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
                 { !embedded  && <span><SafeFormattedMessage msgKey="ATTITUDES_RAW_ATTITUDES_SCORES" /></span> }
                </h3>
                {scores.length > 0 ? (
                  <div style={{ height: '260px', width: '100%', marginLeft: '-100px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {(() => {
                        const chartData = scores.map(att => {
                          const rawScore = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
                          return { name: att.name, value: rawScore, rawScore: rawScore.toFixed(2) };
                        });
                        return (
                          <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis type="number" domain={[0, 4]} ticks={[0,0.5,1,1.5,2,2.5,3,3.5,4]} label={{ value: '', position: 'insideBottom' }} />
                            <YAxis type="category" dataKey="name" width={150} />
                            <Tooltip formatter={(value, name, props) => [props.payload.rawScore, 'Raw Score']} labelFormatter={(label) => `Attitude: ${label}`} />
                            <Bar dataKey="value">
                              {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={'var(--ppe-green)'} />)}
                            </Bar>
                          </BarChart>
                        );
                      })()}
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p style={{ textAlign: 'center', color: '#6c757d' }}><SafeFormattedMessage msgKey="ATTITUDES_NO_ATTITUDE_DATA" /></p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : null;

  const finalPageJsx = (
    <div className="PageCharts">
      <div>
        <div className="PageCharts-chart">
          <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
            { !embedded  && 
              <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '14px', color: '#6c757d' }}>
                <p style={{ margin: 0 }}><strong><SafeFormattedMessage msgKey="ATTITUDES_SCOPE_ASSESSMENT_SYSTEM" /></strong></p>
                <p style={{ margin: '5px 0 0 0' }}><SafeFormattedMessage msgKey="ATTITUDES_CONFIDENTIAL_REPORT" /></p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );

  // If embedded, render only the inner PageCharts block
  if (embedded) return (
    <>
      {page1Jsx}
      {
      !embedded &&
        <div>
          <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>
          {page2Jsx}
          <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>
          {additionalAnalysesJsx}
        </div>
      }
    </>
  );

  // Otherwise render full page with header and navigation
  return (
    <div style={{ backgroundColor: 'white', minHeight: '100vh', color: '#333' }}>
      <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderBottom: '2px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <img src={ppeFullLogo} alt="SCOPE Logo" style={{ height: '40px', width: 'auto' }} />
        <button onClick={onBack} style={{ background: '#6c757d', border: 'none', borderRadius: '6px', padding: '8px 16px', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}><SafeFormattedMessage msgKey="ATTITUDES_BACK_TO_ADMIN" /></button>
      </div>
      <div id="UserInfo">
        <div className="UserInfo-content" style={{ marginTop: '15px' }}>
          {page1Jsx}
          {page2Jsx}
          {additionalAnalysesJsx}
          {!embedded && finalPageJsx}
        </div>
      </div>
    </div>
  );
}
